/**
 * tableModel — the data core of the ported Lilia table tool, now the editor's
 * table module. Model + LaTeX generator + the two importers (Excel/CSV and, new
 * here, LaTeX) + the bridge to/from the editor's `table` block content.
 *
 * Ported from lilia-cloud `components/tools/table/shared.tsx`; keep the two in
 * sync until they converge into one package.
 */

import {
  cellColspan, cellRowspan, cellText, placeRow, plain, rowWidth, tableWidth, toCell, toCells,
  type Cell, type LooseCell,
} from "./tableCells";
import {
  escapeUnit, headerWithUnit, isBestText, isBlank, numberLatex, parseNumber, planColumns, sColumnSpec, stripUnit,
  type Best,
} from "./tableFormat";
import {
  groupStarts, isLongTable, noteMarks, remapGroups, validGroups,
  type HeaderGroup,
} from "./tableLayout";

export { cellText, cellColspan, cellRowspan, plain, toCell, toCells, rowWidth, tableWidth, placeRow };
export type { Cell, LooseCell, HeaderGroup };

/** `decimal` is an siunitx S column, aligned on the decimal point (2g). */
export type Align = "l" | "c" | "r" | "decimal";
export type { Best };
export type Borders = "booktabs" | "grid" | "none";
export interface RowColors { odd?: string; even?: string }

const HEX = /^#?([0-9a-fA-F]{6})$/;
/** `#RRGGBB` from a stored colour, or undefined. */
export const hexColour = (v: unknown): string | undefined => {
  const m = typeof v === "string" ? HEX.exec(v.trim()) : null;
  return m ? `#${m[1].toUpperCase()}` : undefined;
};
/** The stored rowColors, kept only when one colour is valid. */
export function rowColorsFromBlock(raw: unknown): RowColors | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const odd = hexColour(r.odd);
  const even = hexColour(r.even);
  return odd || even ? { ...(odd ? { odd } : {}), ...(even ? { even } : {}) } : undefined;
}
/** A body row's own colour (`index` from 0), if the table has one for it. */
export const rowColour = (c: RowColors | undefined, index: number): string | undefined =>
  c ? (index % 2 === 0 ? c.odd : c.even) : undefined;

export interface TableData {
  /** Cells the block had and this model cannot hold. Empty or absent for a
   *  table built in the tool; populated only by `blockToTableData`. */
  unsupported?: UnsupportedCell[];
  caption: string;
  label: string;
  hasHeader: boolean;
  borders: Borders;
  align: Align[];
  /**
   * Per column, a unit the header carries (`Top-1 (%)`), stripped from cells
   * that repeat it. Stored as `columnUnit`; absent on tables that have none.
   */
  units?: (string | null)[];
  /** Per column, which value is best and printed bold. Stored as `columnBest`. */
  best?: (Best | null)[];
  /** Group headers above the header row (2g). Stored as `headerGroups`; see `tableLayout.ts`. */
  groups?: HeaderGroup[];
  /** Body rows that start a row group (2g). Stored as `rowGroups`. */
  rowGroups?: number[];
  /** Marked long by an import (`longTable`); a table over 40 rows is long by itself. */
  longTable?: boolean;
  /**
   * The table's own alternating row colours, hex (21 Oct 2026). `odd` is the first body row; the
   * header is never coloured. Stored as `rowColors`; absent on tables that have none.
   */
  rowColors?: RowColors;
  /**
   * Header and body are `Cell[]` now, so a merged cell survives the round trip
   * instead of being flattened on save. A span lives on its anchor cell; the
   * cells it covers are absent from the row entirely (see `tableCells.ts`).
   */
  header: Cell[];
  rows: Cell[][];
}

export const ALIGN_NEXT: Record<Align, Align> = { l: "c", c: "r", r: "decimal", decimal: "l" };
export const ALIGN_ICON: Record<Align, string> = { l: "alignLeft", c: "alignCenter", r: "alignRight", decimal: "alignRight" };
export const BORDERS: { v: Borders; l: string }[] = [
  { v: "booktabs", l: "booktabs" },
  { v: "grid", l: "Full grid" },
  { v: "none", l: "No rules" },
];

export const insert = <T,>(a: T[], at: number, v: T): T[] => { const b = a.slice(); b.splice(at, 0, v); return b; };
export const drop = <T,>(a: T[], i: number): T[] => { const b = a.slice(); b.splice(i, 1); return b; };
export const arrMove = <T,>(a: T[], from: number, to: number): T[] => { const b = a.slice(); const [x] = b.splice(from, 1); b.splice(to, 0, x); return b; };
export const cloneTable = (o: TableData): TableData => JSON.parse(JSON.stringify(o));
export const ta = (a: Align): "left" | "center" | "right" => (a === "l" ? "left" : a === "c" ? "center" : "right");

/**
 * Keep the per-column option arrays in step with a column operation (insert,
 * drop, move). Absent arrays stay absent, so a table without options saves as
 * it always did.
 */
export function withColumnArrays(t: TableData, fn: <T>(a: T[], fill: T) => T[]): Pick<TableData, "units" | "best" | "groups"> {
  const out: Pick<TableData, "units" | "best" | "groups"> = {};
  if (t.units) out.units = fn(t.units, null);
  if (t.best) out.best = fn(t.best, null);
  // Group headers follow their columns too: run the same operation over the
  // column indexes, and keep each group on the run its columns land in.
  if (t.groups?.length) {
    const width = Math.max(t.header.length, ...t.groups.map((g) => g.start + g.span));
    const NEW = -1;
    const moved = fn(Array.from({ length: width }, (_, i) => i), NEW);
    const where = new Map<number, number>();
    const added: number[] = [];
    moved.forEach((old, at) => (old === NEW ? added.push(at) : where.set(old, at)));
    out.groups = remapGroups(t.groups, (old) => where.get(old) ?? null, added);
  }
  return out;
}

const rectangular = (grid: string[][]): string[][] => {
  const cols = Math.max(1, ...grid.map((r) => r.length));
  return grid.map((r) => { const x = r.slice(); while (x.length < cols) x.push(""); return x; });
};

/**
 * LaTeX generator — booktabs / full grid / no rules, with spans.
 *
 * The arithmetic is the part that trips authors up, so it is the generator's
 * problem and not theirs:
 *
 *   - `\multicolumn{2}{c}{…}` consumes two columns, so that row carries one
 *     fewer `&` than a plain row.
 *   - A row under a `\multirow` still emits its own `&` separators; the covered
 *     column is simply empty, because the cell above is still occupying it.
 *   - A header group emits the trimmed `\cmidrule(lr){2-3}` under it, which is
 *     what people actually want when they reach for a partial rule.
 */
/** A cell whose entire content is one \textbf — not a cell that merely has one. */
const BOLD_WHOLE_CELL = /^\\textbf\{[\s\S]*\}$/;

/**
 * A cell that is nothing but maths.
 *
 * `\textbf` switches the TEXT font; anything inside `$…$` is typeset in math mode
 * and does not inherit it. So `\textbf{$\Delta$}` changes no glyph — it is a
 * wrapper that does nothing, sitting in source the author is meant to read.
 * Bolding maths needs \bm or \boldmath, which is a different decision from
 * "headers are bold" and not one to make silently on anyone's behalf.
 */
const MATHS_WHOLE_CELL = /^\$[\s\S]*\$$/;

function cellToLatex(c: Cell, align: Align, borders: Borders, bold = false, content = c.content, mark: string | null = null): string {
  const cs = cellColspan(c);
  const rs = cellRowspan(c);
  let body = content;
  // A column header is bold in the source as well as on screen, so the code and
  // the UI say the same thing — and so this generator agrees with the server's
  // RenderTableToLatex, which has always emitted \textbf for the header row.
  // Never twice: a cell the author already bolded stays singly bold.
  if (bold && body.trim() && !BOLD_WHOLE_CELL.test(body) && !MATHS_WHOLE_CELL.test(body))
    body = `\\textbf{${body}}`;
  // A note's mark follows the text, inside any span (2g).
  if (mark) body += `\\tnote{${mark}}`;
  if (rs > 1) body = `\\multirow{${rs}}{*}{${body}}`;
  if (cs > 1) {
    // Inside a grid table the spanned cell carries its own rules. A span in a
    // decimal column is centred, as siunitx centres text in one.
    const letter = align === "decimal" ? "c" : align;
    const spec = borders === "grid" ? `|${letter}|` : `${letter}`;
    body = `\\multicolumn{${cs}}{${spec}}{${body}}`;
  }
  return body;
}

/** `\cmidrule(lr){a-b}` for each header group, left to right. */
function cmidrulesFor(header: readonly Cell[]): string {
  const { starts } = placeRow(header, []);
  const parts: string[] = [];
  header.forEach((c, i) => {
    const cs = cellColspan(c);
    if (cs > 1) parts.push(`\\cmidrule(lr){${starts[i] + 1}-${starts[i] + cs}}`);
  });
  return parts.join(" ");
}

export function tableToLatex(t: TableData): string {
  const width = tableWidth(t.header, t.rows, t.hasHeader);
  const align: Align[] = Array.from({ length: width }, (_, i) => t.align[i] ?? "l");
  // The table's own meaning (2g): decimal columns as siunitx S, the unit in the
  // header, the best value bold. The same plan the server writes.
  const plan = planColumns(t, width);
  const letters = align.map((a, i) => (plan[i].decimal ? sColumnSpec(plan[i]) : a === "decimal" ? "l" : a));
  const spec = t.borders === "grid" ? "|" + letters.join("|") + "|" : letters.join("");
  // …and the rest of it (tableLayout.ts): group headers, notes, row groups, long tables.
  const { marks, notes } = noteMarks(t);
  const groups = validGroups(t, width);
  const starts = groupStarts(t);
  const long = isLongTable(t);

  // An S column reads anything not braced as a number; a \multicolumn replaces
  // the column type and must come first in its cell, so it stays bare.
  const protect = (col: number, s: string) =>
    plan[col]?.decimal && s && !s.startsWith("\\multicolumn") ? `{${s}}` : s;

  // Walk the body so a continuation row knows which columns are still owned.
  // `r` is the row's index (-1 for the header), which is how a note's mark is found.
  let occupied: number[] = [];
  const renderRow = (row: readonly Cell[], r: number): string => {
    const bold = r === -1;
    const { starts: at, occupied: next } = placeRow(row, occupied);
    occupied = next;
    return row.map((c, i) => {
      const col = at[i];
      const p = plan[col];
      const mark = marks.get(`${r}:${i}`) ?? null;
      if (bold) {
        const unit = p?.unit ? escapeUnit(p.unit) : null;
        return protect(col, cellToLatex(c, align[col] ?? "l", t.borders, true, headerWithUnit(c.content, unit), mark));
      }
      const text = stripUnit(c.content, p?.unit);
      const n = p?.decimal && cellColspan(c) === 1 && cellRowspan(c) === 1 ? parseNumber(text) : null;
      // An S column's number keeps its alignment: the mark is braced after it.
      if (n) return numberLatex(n, n.bold || isBestText(p, text)) + (mark ? `{\\tnote{${mark}}}` : "");
      const best = !p?.decimal && isBestText(p, text) && text.trim() && !BOLD_WHOLE_CELL.test(text) ? `\\textbf{${text}}` : text;
      return protect(col, cellToLatex(c, align[col] ?? "l", t.borders, false, best, mark));
    }).join(" & ") + " \\\\";
  };

  const headLine = t.hasHeader ? renderRow(t.header, -1) : "";
  const headRules = t.hasHeader ? cmidrulesFor(t.header) : "";
  const bodyLines = t.rows.map((row, r) => renderRow(row, r));

  // The group header row: its labels over their columns, a short rule under each
  // (a \cline in a full grid, which has no booktabs rules).
  const groupLines: string[] = [];
  if (groups.length) {
    const cells: string[] = [];
    let col = 0;
    for (const g of groups) {
      for (; col < g.start; col++) cells.push("");
      const label = cellToLatex(plain(g.label), "c", t.borders, true);
      cells.push(`\\multicolumn{${g.span}}{${t.borders === "grid" ? "|c|" : "c"}}{${label}}`);
      col += g.span;
    }
    for (; col < width; col++) cells.push("");
    groupLines.push(cells.join(" & ") + " \\\\");
    groupLines.push(groups.map((g) => t.borders === "grid"
      ? `\\cline{${g.start + 1}-${g.start + g.span}}`
      : `\\cmidrule(lr){${g.start + 1}-${g.start + g.span}}`).join(" "));
  }

  // The rules each preset draws: above the table, under the header, under the
  // table, and before a row group (in a full grid every row has its \hline, so a
  // group gets a second one; with no rules, a little space).
  const rules = t.borders === "booktabs"
    ? { top: "\\toprule", head: "\\midrule", bottom: "\\bottomrule", group: "\\midrule" }
    : t.borders === "grid"
      ? { top: "\\hline", head: "\\hline", bottom: "", group: "\\hline" }
      : { top: "", head: "", bottom: "", group: "\\addlinespace" };
  const head: string[] = [];
  if (t.hasHeader) {
    head.push(...groupLines, headLine);
    // Header spans' trimmed rules: drawn by booktabs and no-rules tables.
    if (headRules && t.borders !== "grid") head.push(headRules);
    if (rules.head) head.push(rules.head);
  }
  const body: string[] = [];
  bodyLines.forEach((b, r) => {
    if (starts.has(r)) body.push(rules.group);
    body.push(b);
    if (t.borders === "grid") body.push("\\hline");
  });
  const noteItems = notes.map((n) => `\\item[${n.mark}] ${n.text}`);

  if (long) {
    // Over 40 rows: a longtable, which breaks across pages. Not a float, so the
    // caption is its first row; the header repeats on every page under
    // "(continued)". Notes go in the last foot (threeparttablex).
    const L: string[] = [];
    if (notes.length) L.push("\\begin{ThreePartTable}", "\\begin{TableNotes}\\footnotesize", ...noteItems.map((x) => "  " + x), "\\end{TableNotes}");
    L.push("\\begin{longtable}{" + spec + "}");
    const I = "  ";
    const captionRow = (t.caption ? "\\caption{" + t.caption + "}" : "") + (t.label ? "\\label{" + t.label + "}" : "");
    if (captionRow) L.push(I + captionRow + "\\\\");
    if (rules.top) L.push(I + rules.top);
    if (head.length) {
      head.forEach((x) => L.push(I + x));
      L.push(I + "\\endfirsthead");
      if (t.caption) L.push(I + "\\caption[]{(continued)}\\\\");
      if (rules.top) L.push(I + rules.top);
      head.forEach((x) => L.push(I + x));
      L.push(I + "\\endhead");
    }
    if (notes.length) {
      if (rules.bottom) L.push(I + rules.bottom);
      L.push(I + "\\insertTableNotes", I + "\\endlastfoot");
    }
    body.forEach((x) => L.push(I + x));
    if (rules.bottom && !notes.length) L.push(I + rules.bottom);
    L.push("\\end{longtable}");
    if (notes.length) L.push("\\end{ThreePartTable}");
    return L.join("\n");
  }

  const I = "    ";
  const L: string[] = ["\\begin{table}[t]", "  \\centering"];
  if (t.caption) L.push("  \\caption{" + t.caption + "}");
  if (t.label) L.push("  \\label{" + t.label + "}");
  // Notes print under the table, not at the page foot: threeparttable.
  if (notes.length) L.push("  \\begin{threeparttable}");
  L.push("  \\begin{tabular}{" + spec + "}");
  if (rules.top) L.push(I + rules.top);
  head.forEach((x) => L.push(I + x));
  body.forEach((x) => L.push(I + x));
  if (rules.bottom) L.push(I + rules.bottom);
  L.push("  \\end{tabular}");
  if (notes.length) L.push("  \\begin{tablenotes}\\footnotesize", ...noteItems.map((x) => I + x), "  \\end{tablenotes}", "  \\end{threeparttable}");
  L.push("\\end{table}");
  return L.join("\n");
}

/** Parse an Excel / Google Sheets / CSV paste block into a rectangular grid. */
export function parsePaste(text: string): string[][] | null {
  if (!text || !(text.includes("\t") || text.includes("\n"))) return null;
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.length > 0);
  if (!lines.length) return null;
  const delim = lines[0].includes("\t") ? "\t" : ",";
  return rectangular(lines.map((l) => l.split(delim).map((c) => c.trim())));
}

/**
 * Parse pasted LaTeX (a `tabular`/`table`, or a bare row block) into a TableData.
 * New capability — the tool only *exported* LaTeX. Handles booktabs
 * (\toprule/\midrule/\bottomrule), \hline grids, the `{lcr|}` column spec,
 * `&` cells, `\\` rows, and \caption/\label. Returns null if it isn't a table.
 */
/**
 * Read one pasted cell, lifting `\\multicolumn` and `\\multirow` into the span
 * fields rather than leaving the command sitting in the text.
 *
 * They nest either way round in the wild — `\\multicolumn{2}{c}{\\multirow{2}{*}{x}}`
 * and the reverse both occur — so this unwraps repeatedly until neither matches.
 */
export function parseSpannedCell(raw: string): Cell {
  let body = (raw ?? "").trim();
  let colspan = 1;
  let rowspan = 1;

  for (let guard = 0; guard < 4; guard++) {
    const mc = body.match(/^\\multicolumn\s*\{\s*(\d+)\s*\}\s*\{[^}]*\}\s*\{([\s\S]*)\}$/);
    if (mc) {
      colspan *= Math.max(1, parseInt(mc[1], 10) || 1);
      body = mc[2].trim();
      continue;
    }
    const mr = body.match(/^\\multirow\s*\{\s*(\d+)\s*\}\s*\{[^}]*\}\s*(?:\[[^\]]*\])?\s*\{([\s\S]*)\}$/);
    if (mr) {
      rowspan *= Math.max(1, parseInt(mr[1], 10) || 1);
      body = mr[2].trim();
      continue;
    }
    break;
  }

  const cell: Cell = { content: body };
  if (colspan > 1) cell.colspan = colspan;
  if (rowspan > 1) cell.rowspan = rowspan;
  return cell;
}

export function parseLatexTable(input: string): TableData | null {
  if (!input) return null;
  let src = input.trim();

  // A table's notes (threeparttable's tablenotes, threeparttablex's TableNotes),
  // by mark, so each cell's \tnote comes back as that cell's note (2g).
  const noteText = new Map<string, string>();
  for (const env of src.matchAll(/\\begin\{(tablenotes|TableNotes)\}(?:\[[^\]]*\])?([\s\S]*?)\\end\{\1\}/g)) {
    for (const item of env[2].matchAll(/\\item\s*\[([^\]]*)\]\s*([\s\S]*?)(?=\\item\b|$)/g)) {
      const text = item[2].replace(/\\footnotesize\b|\\small\b/g, "").replace(/\s+/g, " ").trim();
      if (item[1].trim() && text && !noteText.has(item[1].trim())) noteText.set(item[1].trim(), text);
    }
  }
  src = src.replace(/\\begin\{(tablenotes|TableNotes)\}[\s\S]*?\\end\{\1\}/g, "");

  const caption = src.match(/\\caption\{([^}]*)\}/)?.[1]?.trim() ?? "";
  const label = src.match(/\\label\{([^}]*)\}/)?.[1]?.trim() ?? "";

  // Column spec from \begin{tabular}{...}, \begin{longtable}{...} or
  // \begin{array}{...}. Braces may nest two deep: an S column's options carry
  // `group-separator={,}`.
  const specMatch = src.match(/\\begin\{(?:tabular|longtable|array)\}\s*(?:\[[^\]]*\])?\s*\{((?:[^{}]|\{(?:[^{}]|\{[^{}]*\})*\})*)\}/);
  const grid = /\|/.test(specMatch?.[1] ?? "");
  const booktabs = /\\(top|mid|bottom)rule/.test(src);

  // Narrow to the tabular body if present.
  if (specMatch?.index !== undefined) {
    const rest = src.slice(specMatch.index + specMatch[0].length);
    const end = rest.search(/\\end\{(?:tabular|longtable|array)\}/);
    if (end >= 0) src = rest.slice(0, end);
  }
  // A longtable repeats its head on later pages (a copy of the first, under
  // "(continued)"), and prints its notes in the last foot: neither is a row.
  src = src
    .replace(/\\endfirsthead[\s\S]*?\\endhead\b/g, "")
    .replace(/\\(?:insertTableNotes|endlastfoot|endfoot|endhead)\b/g, "");
  // The rules that carry meaning are marked before the rules are stripped: a
  // \midrule (the header's, then each row group's), the space or second \hline
  // a row group gets without booktabs, and the trimmed rule under a group header.
  const RULE = "\u0001", GROUP = "\u0002", TRIM = "\u0003";
  src = src
    .replace(/\\hline\s*\\hline/g, GROUP)
    .replace(/\\midrule(\[[^\]]*\])?/g, RULE)
    .replace(/\\addlinespace(\[[^\]]*\])?/g, GROUP)
    .replace(/\\cmidrule\s*(\([a-z]*\))?\s*\{[^}]*\}|\\cline\{[^}]*\}/g, TRIM);
  // Strip rule/hline commands and the table wrapper leftovers.
  //
  // `\cmidrule` has to be listed on its own: `\\(top|mid|bottom)rule` does not
  // match it, because the backslash is followed by `c`. Left in, the whole
  // `\cmidrule(lr){2-3}` survived to the cell split and became the first cell
  // of the next row — a rule command sitting in the table as data, with nothing
  // reporting it.
  src = src
    .replace(/\\(top|mid|bottom)rule(\[[^\]]*\])?/g, "")
    .replace(/\\cmidrule\s*(\([a-z]*\))?\s*\{[^}]*\}/g, "")
    .replace(/\\specialrule\{[^}]*\}\{[^}]*\}\{[^}]*\}/g, "")
    .replace(/\\addlinespace(\[[^\]]*\])?/g, "")
    .replace(/\\hline/g, "")
    .replace(/\\cline\{[^}]*\}/g, "")
    .replace(/\\begin\{(table|center|threeparttable|ThreePartTable)\}(\[[^\]]*\])?/g, "")
    .replace(/\\end\{(table|center|tabular|array|longtable|threeparttable|ThreePartTable)\}/g, "")
    .replace(/\\centering/g, "")
    .replace(/\\caption(\[[^\]]*\])?\{[^}]*\}/g, "")
    .replace(/\\label\{[^}]*\}/g, "");

  // Each row with the marks that stood before it.
  const marked = src.split(/\\\\/).map((r) => ({
    rule: r.includes(RULE), group: r.includes(GROUP), trim: r.includes(TRIM),
    text: r.replaceAll(RULE, "").replaceAll(GROUP, "").replaceAll(TRIM, "").trim(),
  })).filter((r) => r.text.length > 0);
  if (marked.length === 0) return null;
  const rowStrings = marked.map((r) => r.text);

  const rows = rowStrings.map((r) => r.split("&").map((c) => c.trim().replace(/\s+/g, " ")));
  // Must look like an actual table: >1 column somewhere.
  if (!rows.some((r) => r.length > 1) && rows.length < 2) return null;

  // A group header row: the first row, all \multicolumn labels and empty cells,
  // with the trimmed rules under it, above the real header (2g).
  const isGroupRow = rows.length > 2 && marked[1].trim
    && (!booktabs || !marked[1].rule)
    && rows[0].some((c) => /^\\multicolumn\s*\{/.test(cleanLiliaCell(c)))
    && rows[0].every((c) => { const x = cleanLiliaCell(c); return x === "" || /^\\multicolumn\s*\{/.test(x); });
  const groups: HeaderGroup[] = [];
  if (isGroupRow) {
    let col = 0;
    for (const raw of rows[0]) {
      const cell = parseSpannedCell(cleanLiliaCell(raw));
      const span = cell.colspan ?? 1;
      if (cleanLiliaCell(raw)) groups.push({ label: cleanLiliaCell(cell.content).replace(/^\\textbf\{([\s\S]*)\}$/, "$1"), start: col, span });
      col += span;
    }
    rows.shift();
    marked.shift();
  }

  // Row groups: a \midrule after the header's own, or a group mark, before a body row.
  const rowGroups: number[] = [];
  let headRuleSeen = !booktabs;
  marked.forEach((m, k) => {
    if (k === 0) return;
    if (m.rule && !headRuleSeen) { headRuleSeen = true; return; }
    if ((m.rule || m.group) && k - 1 > 0) rowGroups.push(k - 1);
  });

  const grid2 = rectangular(rows);
  const ncol = grid2[0].length;

  // A cell's \tnote mark becomes its note; in an S column the mark is braced after the number.
  const withNote = (raw: string): { text: string; note?: string } => {
    let mark: string | null = null;
    const text = raw.replace(/\{\\tnote\{([^}]*)\}\}|\\tnote\{([^}]*)\}/g, (_m, a?: string, b?: string) => {
      mark ??= (a ?? b ?? "").trim();
      return "";
    }).trim();
    const note = mark ? noteText.get(mark) : undefined;
    return note ? { text, note } : { text };
  };

  const [header0, ...restRows] = grid2.map((r) => r.map((c) => {
    const { text, note } = withNote(c);
    const cell = parseSpannedCell(cleanLiliaCell(text));
    return { ...cell, content: cleanLiliaCell(cell.content), ...(note ? { note } : {}) };
  }));
  // The generator bolds the header row, so reading it back has to unbold it —
  // otherwise every Typeset → Take it cycle adds another \textbf.
  const header = header0.map((c) =>
    BOLD_WHOLE_CELL.test(c.content)
      ? { ...c, content: c.content.replace(/^\\textbf\{([\s\S]*)\}$/, "$1") }
      : c,
  );
  const hasHeader = grid2.length > 1;

  // Column alignments from the spec (l/c/r), ignoring | and sizing args. An
  // siunitx S column is decimal (2g); one holding nothing but numbers reads
  // back as the default, because that is what decimal-aligns it by itself —
  // so a table the tool wrote comes back exactly as it went out.
  const specCols = specColumns(specMatch?.[1] ?? "");
  const body = hasHeader ? restRows : [];
  const align: Align[] = Array.from({ length: ncol }, (_, i) => {
    const ch = specCols[i];
    if (ch === "S") {
      const vals = body.map((r) => r[i]?.content ?? "").filter((v) => !isBlank(v));
      return vals.length >= 2 && vals.every((v) => parseNumber(v)) ? "l" : "decimal";
    }
    return ch === "l" || ch === "c" || ch === "r" ? (ch as Align) : "l";
  });
  return {
    caption,
    label,
    hasHeader,
    borders: booktabs ? "booktabs" : grid ? "grid" : "none",
    align,
    ...(groups.length && hasHeader ? { groups } : {}),
    ...(rowGroups.length && hasHeader ? { rowGroups } : {}),
    header,
    rows: hasHeader ? restRows : [header.map(() => plain(""))],
  };
}

/**
 * One column letter per column of a tabular spec: `|` and spacing dropped,
 * `p{3cm}` (and m, b) read as `l`, an option list (`S[table-format=2.1]`) and
 * `@{…}`/`>{…}`/`<{…}` skipped.
 */
function specColumns(spec: string): string[] {
  const out: string[] = [];
  const skipGroup = (i: number, open: string, close: string): number => {
    if (spec[i] !== open) return i;
    let depth = 0;
    for (; i < spec.length; i++) {
      if (spec[i] === open) depth++;
      else if (spec[i] === close && --depth === 0) return i + 1;
    }
    return i;
  };
  let i = 0;
  while (i < spec.length) {
    const ch = spec[i];
    if (ch === "|" || /\s/.test(ch)) { i++; continue; }
    if (ch === "@" || ch === ">" || ch === "<" || ch === "!") { i = skipGroup(i + 1, "{", "}"); continue; }
    i = skipGroup(i + 1, "[", "]");
    const sized = spec[i] === "{";
    i = skipGroup(i, "{", "}");
    out.push(sized && (ch === "p" || ch === "m" || ch === "b") ? "l" : ch);
  }
  return out;
}

/**
 * Lilia's own table markup, so a table the tool or the server wrote pastes back
 * as its cells: the theme hooks (`\liliaHeadRow`, `\liliaTableHead{…}`), the
 * braces an S column puts round text, a best value's `\bfseries`, and a `%`
 * braced after a number.
 */
function cleanLiliaCell(raw: string): string {
  let s = raw.replace(/\\liliaHeadRow\b\s*/g, "").trim();
  for (let guard = 0; guard < 3; guard++) {
    const before = s;
    if (s.startsWith("{") && closingBrace(s, 0) === s.length - 1) s = s.slice(1, -1).trim();
    const head = s.match(/^\\liliaTableHead\{/);
    if (head && closingBrace(s, head[0].length - 1) === s.length - 1) s = s.slice(head[0].length, -1).trim();
    if (s === before) break;
  }
  return s.replace(/^\\bfseries\s+/, "").replace(/\{\\%\}/g, "%");
}

/** Index of the brace closing the one at `open`, or -1. */
function closingBrace(s: string, open: number): number {
  let depth = 0;
  for (let i = open; i < s.length; i++) {
    if (s[i] === "\\") { i++; continue; }
    if (s[i] === "{") depth++;
    else if (s[i] === "}" && --depth === 0) return i;
  }
  return -1;
}

/** Heuristic: does a paste look like LaTeX table source (vs Excel/CSV)? */
export function looksLikeLatexTable(text: string): boolean {
  return /\\begin\{(tabular|array|table)\}/.test(text) || (/\\\\/.test(text) && /&/.test(text));
}

// ── Bridge: editor `table` block content ⇄ TableData ─────────────────────────
// The block stores { headers, rows, columnAlign, caption, label, borders? }.
// Cells may be strings or { text } objects; normalize to strings.

const cellToString = (c: unknown): string =>
  typeof c === "string" ? c : c && typeof c === "object" && "text" in c ? String((c as { text?: unknown }).text ?? "") : "";

const normAlign = (a: unknown): Align => {
  const s = String(a ?? "").toLowerCase();
  if (s === "c" || s === "center") return "c";
  if (s === "r" || s === "right") return "r";
  if (s === "decimal" || s === "d" || s === "s") return "decimal";
  return "l";
};

/**
 * A cell this model cannot represent. `TableData.rows` is `string[][]`, so a
 * merged cell has nowhere to go — but the in-document block DOES support spans
 * (`TableBlock.tsx` declares `colspan` / `rowspan` on its `CellData`), so a
 * table arriving from a document can carry them.
 *
 * They used to be dropped in silence by `cellToString`, which keeps only
 * `.text`. Round-tripping a real table through the tool would have flattened
 * every merged cell in it without a word. They are reported now, and
 * `findTableProblems` turns each into an error the author can see.
 *
 * This is deliberately NOT support for spans — representing them is a model
 * change, and a design decision that has not been taken
 * (design-handoffs/2026-09-18-table-tool, Q4). This only makes the loss loud.
 */
export interface UnsupportedCell {
  /** -1 is the header row. */
  row: number;
  col: number;
  colspan: number;
  rowspan: number;
}

export interface TableBlockContent {
  headers?: unknown[];
  rows?: unknown[][];
  columnAlign?: unknown[];
  /** Per column: a unit the header carries (2g). */
  columnUnit?: unknown[];
  /** Per column: "higher" | "lower" — the best value is bold (2g). */
  columnBest?: unknown[];
  caption?: string;
  label?: string;
  borders?: Borders;
  hasHeader?: boolean;
  /** Group headers above the header row (2g). */
  headerGroups?: unknown[];
  /** Body rows that start a row group (2g). */
  rowGroups?: unknown[];
  /** Marked long by an import. */
  longTable?: boolean;
  /** The table's own alternating row colours: { odd, even }, hex. */
  rowColors?: unknown;
}

/**
 * Read the block's content into the model.
 *
 * This used to flatten every merged cell to its text and record the loss in
 * `unsupported[]`, because `rows` was `string[][]`. It is now `Cell[][]`, so the
 * spans come through intact and there is nothing to record: a Word-imported
 * table with a merged header opens, edits and saves as itself.
 *
 * `unsupported` stays on the type, empty, so the Problems list keeps its shape
 * for the next thing we cannot represent.
 */
export function blockToTableData(content: TableBlockContent | undefined | null): TableData {
  const header = toCells(
    (content?.headers as LooseCell[] | undefined) ?? ["Column 1", "Column 2", "Column 3"],
  );
  const rawRows = (content?.rows as LooseCell[][] | undefined) ?? [header.map(() => plain(""))];
  const rows = rawRows.map((r) => (Array.isArray(r) ? toCells(r) : []));
  const ncol = tableWidth(header, rows, content?.hasHeader ?? true);
  const align: Align[] = Array.from({ length: ncol }, (_, i) => normAlign(content?.columnAlign?.[i]));
  return {
    unsupported: [],
    caption: content?.caption ?? "",
    label: content?.label ?? "",
    hasHeader: content?.hasHeader ?? true,
    borders: content?.borders ?? "booktabs",
    align,
    ...columnOptionsFromBlock(content, ncol),
    ...layoutFromBlock(content),
    ...(rowColorsFromBlock(content?.rowColors) ? { rowColors: rowColorsFromBlock(content?.rowColors) } : {}),
    header,
    rows: rows.length ? rows : [header.map(() => plain(""))],
  };
}

/**
 * The 2g layout as stored (`headerGroups`, `rowGroups`, `longTable`), read into
 * the model. Absent when the table has none, so it round-trips unchanged. Notes
 * live on their cells and come through with them.
 */
export function layoutFromBlock(content: { headerGroups?: unknown; rowGroups?: unknown; longTable?: unknown } | null | undefined): Pick<TableData, "groups" | "rowGroups" | "longTable"> {
  const groups = Array.isArray(content?.headerGroups)
    ? content.headerGroups
        .filter((g): g is Record<string, unknown> => !!g && typeof g === "object")
        .map((g) => ({ label: typeof g.label === "string" ? g.label : "", start: Number(g.start), span: Number(g.span) }))
        .filter((g) => Number.isInteger(g.start) && Number.isInteger(g.span) && g.start >= 0 && g.span >= 1)
    : [];
  const rowGroups = Array.isArray(content?.rowGroups)
    ? content.rowGroups.filter((i): i is number => Number.isInteger(i) && (i as number) > 0)
    : [];
  return {
    ...(groups.length ? { groups } : {}),
    ...(rowGroups.length ? { rowGroups } : {}),
    ...(content?.longTable === true ? { longTable: true } : {}),
  };
}

/** The model's 2g layout as stored; empty when it has none. */
export function layoutToBlock(t: Pick<TableData, "groups" | "rowGroups" | "longTable">): Pick<TableBlockContent, "headerGroups" | "rowGroups" | "longTable"> {
  return {
    ...(t.groups?.length ? { headerGroups: t.groups.map((g) => ({ label: g.label, start: g.start, span: g.span })) } : {}),
    ...(t.rowGroups?.length ? { rowGroups: [...t.rowGroups] } : {}),
    ...(t.longTable ? { longTable: true } : {}),
  };
}

/**
 * The 2g column options as stored (`columnUnit`, `columnBest`), read into the
 * model. Absent when the table has none, so it round-trips unchanged.
 */
export function columnOptionsFromBlock(content: { columnUnit?: unknown; columnBest?: unknown } | null | undefined, ncol: number): Pick<TableData, "units" | "best"> {
  const units = readColumnArray(content?.columnUnit, ncol, (v) => (typeof v === "string" && v.trim() ? v.trim() : null));
  const best = readColumnArray<Best>(content?.columnBest, ncol, (v) => (v === "higher" || v === "lower" ? v : null));
  return { ...(units ? { units } : {}), ...(best ? { best } : {}) };
}

/** The model's column options as stored; empty when no column has one. */
export function columnOptionsToBlock(t: Pick<TableData, "units" | "best">): Pick<TableBlockContent, "columnUnit" | "columnBest"> {
  const units = t.units?.some((u) => u) ? t.units.map((u) => u || null) : undefined;
  const best = t.best?.some((b) => b) ? t.best.map((b) => b || null) : undefined;
  return { ...(units ? { columnUnit: units } : {}), ...(best ? { columnBest: best } : {}) };
}

/** A stored per-column array, padded to the table's width; undefined when it holds nothing. */
function readColumnArray<T>(raw: unknown, ncol: number, read: (v: unknown) => T | null): (T | null)[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out = Array.from({ length: ncol }, (_, i) => read(raw[i]));
  return out.some((v) => v !== null) ? out : undefined;
}

/** True when writing this table back to a block would flatten merged cells
 *  the block originally had. Check before saving, not after. */
export function wouldFlattenSpans(t: TableData): boolean {
  return (t.unsupported?.length ?? 0) > 0;
}

export function tableDataToBlock(t: TableData): TableBlockContent {
  return {
    headers: t.header,
    rows: t.rows,
    columnAlign: t.align,
    // Written only when a column has one, so a table without them saves exactly as before.
    ...columnOptionsToBlock(t),
    ...layoutToBlock(t),
    ...(t.rowColors && (t.rowColors.odd || t.rowColors.even) ? { rowColors: { ...t.rowColors } } : {}),
    caption: t.caption,
    label: t.label,
    borders: t.borders,
    hasHeader: t.hasHeader,
  };
}

/* ------------------------------------------------------------------------- *
 * Cell formatting, as pure functions.
 *
 * The grid used to own these inside a closure, which meant the only way to
 * bold a cell was to press the button inside the grid's own footer. The
 * context bar needs the same operation from outside, so the logic lives here
 * and both call it. Toggling, not wrapping: pressing B on an already-bold cell
 * unwraps it.
 * ------------------------------------------------------------------------- */

export type CellFormat = "bold" | "math";

const BOLD_RE = /^\\textbf\{([\s\S]*)\}$/;
const MATH_RE = /^\$([\s\S]*)\$$/;

/** Is this cell already wrapped in the given format? */
export function cellHasFormat(v: string, kind: CellFormat): boolean {
  return (kind === "bold" ? BOLD_RE : MATH_RE).test(v ?? "");
}

export function toggleCellFormat(v: string, kind: CellFormat): string {
  const s = v ?? "";
  if (kind === "bold") return BOLD_RE.test(s) ? s.replace(BOLD_RE, "$1") : `\\textbf{${s}}`;
  return MATH_RE.test(s) ? s.replace(MATH_RE, "$1") : `$${s}$`;
}

/** Read one cell. Row -1 is the header, matching CellRef elsewhere. */
export function readCell(t: TableData, r: number, c: number): string {
  return cellText(r === -1 ? t.header[c] : t.rows[r]?.[c]);
}

/** Write one cell, returning a new table. */
export function writeCell(t: TableData, r: number, c: number, v: string): TableData {
  // Writing text must not disturb the cell's span.
  if (r === -1) {
    const header = t.header.slice();
    if (!header[c]) return t;
    header[c] = { ...header[c], content: v };
    return { ...t, header };
  }
  const rows = t.rows.map((x) => x.slice());
  if (!rows[r]?.[c]) return t;
  rows[r][c] = { ...rows[r][c], content: v };
  return { ...t, rows };
}

export function applyCellFormat(t: TableData, r: number, c: number, kind: CellFormat): TableData {
  return writeCell(t, r, c, toggleCellFormat(readCell(t, r, c), kind));
}

/* ------------------------------------------------------------------------- *
 * Preview tokens.
 *
 * A cell's string IS its LaTeX, so the Preview pane has to read it. This is a
 * deliberately small parser: `\textbf{…}` and `$…$`, the only two things the
 * product can produce and — per the corpus measurement — the only two that
 * appear in real cells. Anything else passes through as text rather than being
 * silently swallowed.
 * ------------------------------------------------------------------------- */

export type PreviewToken =
  | { kind: "text"; value: string }
  | { kind: "math"; value: string }
  | { kind: "bold"; value: string };

export function previewTokens(cell: string): PreviewToken[] {
  const out: PreviewToken[] = [];
  const src = cell ?? "";
  let i = 0;
  let plain = "";
  const flush = () => {
    if (plain) out.push({ kind: "text", value: plain });
    plain = "";
  };

  while (i < src.length) {
    if (src.startsWith("\\textbf{", i)) {
      // Walk to the matching brace so `\textbf{a{b}c}` survives.
      let depth = 0;
      let j = i + "\\textbf".length;
      const start = j + 1;
      for (; j < src.length; j++) {
        if (src[j] === "{") depth++;
        else if (src[j] === "}") {
          depth--;
          if (depth === 0) break;
        }
      }
      if (depth === 0 && j <= src.length) {
        flush();
        out.push({ kind: "bold", value: src.slice(start, j) });
        i = j + 1;
        continue;
      }
    }
    if (src[i] === "$") {
      const end = src.indexOf("$", i + 1);
      if (end > i) {
        flush();
        out.push({ kind: "math", value: src.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    plain += src[i];
    i++;
  }
  flush();
  return out;
}

/**
 * Maths, made readable for the Preview pane.
 *
 * Deliberately a lookup, not a renderer: the common Greek letters and
 * relational symbols that appear in table cells, plus `^`/`_` for simple
 * super- and subscripts. Anything it does not know is returned untouched, so an
 * unknown macro shows as itself rather than disappearing — a preview that
 * silently drops what it cannot read is worse than one that admits it.
 *
 * KaTeX would be the real answer; it is not loaded in the tool yet.
 */
const MATH_SYMBOLS: Record<string, string> = {
  alpha: "\u03b1", beta: "\u03b2", gamma: "\u03b3", delta: "\u03b4",
  epsilon: "\u03b5", varepsilon: "\u03b5", zeta: "\u03b6", eta: "\u03b7",
  theta: "\u03b8", kappa: "\u03ba", lambda: "\u03bb", mu: "\u03bc",
  nu: "\u03bd", xi: "\u03be", pi: "\u03c0", rho: "\u03c1", sigma: "\u03c3",
  tau: "\u03c4", phi: "\u03c6", chi: "\u03c7", psi: "\u03c8", omega: "\u03c9",
  Gamma: "\u0393", Delta: "\u0394", Theta: "\u0398", Lambda: "\u039b",
  Xi: "\u039e", Pi: "\u03a0", Sigma: "\u03a3", Phi: "\u03a6", Psi: "\u03a8",
  Omega: "\u03a9",
  times: "\u00d7", cdot: "\u00b7", pm: "\u00b1", mp: "\u2213",
  leq: "\u2264", le: "\u2264", geq: "\u2265", ge: "\u2265", neq: "\u2260",
  approx: "\u2248", sim: "\u223c", infty: "\u221e", partial: "\u2202",
  rightarrow: "\u2192", to: "\u2192", leftarrow: "\u2190",
  ldots: "\u2026", dots: "\u2026",
};

const SUP: Record<string, string> = {
  "0": "\u2070", "1": "\u00b9", "2": "\u00b2", "3": "\u00b3", "4": "\u2074",
  "5": "\u2075", "6": "\u2076", "7": "\u2077", "8": "\u2078", "9": "\u2079",
  "+": "\u207a", "-": "\u207b", n: "\u207f", i: "\u2071",
};
const SUB: Record<string, string> = {
  "0": "\u2080", "1": "\u2081", "2": "\u2082", "3": "\u2083", "4": "\u2084",
  "5": "\u2085", "6": "\u2086", "7": "\u2087", "8": "\u2088", "9": "\u2089",
  "+": "\u208a", "-": "\u208b",
};

export function mathPretty(src: string): string {
  let out = (src ?? "").replace(/\\([A-Za-z]+)/g, (m, name: string) =>
    MATH_SYMBOLS[name] ?? m,
  );
  // ^{ab} / ^a and the subscript equivalents, only where every character maps.
  out = out.replace(/\^\{?([^{}\s]+)\}?/g, (m, body: string) => {
    const mapped = [...body].map((ch) => SUP[ch]);
    return mapped.every(Boolean) ? mapped.join("") : m;
  });
  out = out.replace(/_\{?([^{}\s]+)\}?/g, (m, body: string) => {
    const mapped = [...body].map((ch) => SUB[ch]);
    return mapped.every(Boolean) ? mapped.join("") : m;
  });
  return out.replace(/[{}]/g, "");
}
